// src/services/assessmentService.js
// Updated with Pre-Marriage Intensive Module Support
// Handles both 20-Factor Assessment AND 10-Module Intensive

import { 
  doc, 
  getDoc, 
  setDoc, 
  updateDoc, 
  serverTimestamp,
  arrayUnion 
} from 'firebase/firestore';
import { db } from '../firebase/config';

// ==========================================
// ASSESSMENT INITIALIZATION
// ==========================================

/**
 * Initialize a new assessment for a user
 * Supports both 20-factor and 10-module intensive systems
 */
export const initializeAssessment = async (userId, coupleId) => {
  try {
    const assessmentData = {
      userId,
      coupleId,
      // 20-Factor Assessment Data
      answers: {},              // Will hold answers for questions 1-200
      factorScores: {},         // Will hold scores for factors 1-20 (each out of 50)
      completedFactors: [],     // Array of completed factor numbers [1, 2, 3, ...]
      totalScore: 0,            // Sum of all factor scores (out of 1000)
      
      // Pre-Marriage Intensive Data (10 Modules)
      intensiveAnswers: {},             // Will hold answers for intensive questions 1-160
      intensiveModuleScores: {},        // Will hold scores for modules 1-10
      completedIntensiveModules: [],    // Array of completed module numbers [1, 2, 3, ...]
      intensiveTotalScore: 0,           // Sum of all module scores
      
      status: 'in-progress',
      lastQuestion: 0,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    };

    await setDoc(doc(db, 'assessments', userId), assessmentData);
    return { success: true, data: assessmentData, error: null };
  } catch (error) {
    console.error('Error initializing assessment:', error);
    return { success: false, data: null, error: error.message };
  }
};

/**
 * Get assessment data for a user
 */
export const getAssessment = async (userId) => {
  try {
    const assessmentDoc = await getDoc(doc(db, 'assessments', userId));
    
    if (assessmentDoc.exists()) {
      return { success: true, data: assessmentDoc.data(), error: null };
    }
    
    return { success: false, data: null, error: 'Assessment not found' };
  } catch (error) {
    console.error('Error getting assessment:', error);
    return { success: false, data: null, error: error.message };
  }
};

// ==========================================
// 20-FACTOR ASSESSMENT FUNCTIONS
// ==========================================

/**
 * Save answer for a specific question (20-factor system)
 */
export const saveAnswer = async (userId, questionId, value) => {
  try {
    await updateDoc(doc(db, 'assessments', userId), {
      [`answers.${questionId}`]: value,
      lastQuestion: questionId,
      updatedAt: serverTimestamp()
    });

    return { success: true, error: null };
  } catch (error) {
    console.error('Error saving answer:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Calculate score for a specific factor
 */
export const calculateFactorScore = (answers, factorNumber) => {
  const startQuestion = (factorNumber - 1) * 10 + 1;
  const endQuestion = factorNumber * 10;
  
  let score = 0;
  for (let i = startQuestion; i <= endQuestion; i++) {
    score += answers[i] || 0;
  }
  
  return score;
};

/**
 * Calculate total score across all completed factors
 */
export const calculateTotalScore = (factorScores) => {
  return Object.values(factorScores).reduce((sum, score) => sum + (score || 0), 0);
};

/**
 * Complete a factor and update scores
 */
export const completeFactor = async (userId, factorNumber, factorScore, coupleId = null) => {
  try {
    const assessmentDoc = await getDoc(doc(db, 'assessments', userId));
    if (!assessmentDoc.exists()) {
      return { success: false, error: 'Assessment not found' };
    }

    const currentData = assessmentDoc.data();
    const updatedFactorScores = {
      ...currentData.factorScores,
      [factorNumber]: factorScore
    };

    const totalScore = calculateTotalScore(updatedFactorScores);

    await updateDoc(doc(db, 'assessments', userId), {
      [`factorScores.${factorNumber}`]: factorScore,
      completedFactors: arrayUnion(factorNumber),
      totalScore: totalScore,
      updatedAt: serverTimestamp()
    });

    // Auto-unlock next factor
    if (coupleId && factorNumber > 1 && factorNumber < 20) {
      const nextFactor = factorNumber + 1;
      const coupleRef = doc(db, 'couples', coupleId);
      
      try {
        await updateDoc(coupleRef, {
          [`factorPermissions.${nextFactor}.canTake`]: true,
          [`factorPermissions.${nextFactor}.unlockedAt`]: serverTimestamp()
        });
        console.log(`✅ Auto-unlocked Factor ${nextFactor}`);
      } catch (unlockError) {
        console.error(`Error auto-unlocking Factor ${nextFactor}:`, unlockError);
      }
    }

    return { success: true, totalScore, error: null };
  } catch (error) {
    console.error('Error completing factor:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Get interpretation level for a factor score
 */
export const getFactorInterpretationLevel = (score) => {
  if (score >= 41 && score <= 50) return 'excellent';
  if (score >= 31 && score <= 40) return 'developing';
  return 'vulnerable';
};

/**
 * Get interpretation for overall score
 */
export const getOverallInterpretation = (totalScore) => {
  if (totalScore >= 701 && totalScore <= 1000) {
    return {
      level: 'Strong Compatibility',
      color: 'green',
      message: 'Excellent foundation for marriage with strong alignment across most areas.',
      recommendation: 'Continue building on your strengths and maintain open communication.'
    };
  } else if (totalScore >= 401 && totalScore <= 700) {
    return {
      level: 'Moderate Compatibility',
      color: 'yellow',
      message: 'Good foundation with some areas needing attention and growth.',
      recommendation: 'Focus on developing weaker areas through counseling and intentional practice.'
    };
  } else {
    return {
      level: 'Needs Significant Work',
      color: 'red',
      message: 'Several areas require substantial attention before proceeding with marriage.',
      recommendation: 'Strongly recommend extended counseling and working on identified areas before marriage.'
    };
  }
};

/**
 * Get progress statistics for a user
 */
export const getProgressStats = (assessment) => {
  if (!assessment) {
    return {
      questionsAnswered: 0,
      totalQuestions: 200,
      factorsCompleted: 0,
      totalFactors: 20,
      currentScore: 0,
      maxScore: 1000,
      percentComplete: 0
    };
  }

  const questionsAnswered = Object.keys(assessment.answers || {}).length;
  const factorsCompleted = assessment.completedFactors?.length || 0;
  const currentScore = assessment.totalScore || 0;
  const percentComplete = Math.round((factorsCompleted / 20) * 100);

  return {
    questionsAnswered,
    totalQuestions: 200,
    factorsCompleted,
    totalFactors: 20,
    currentScore,
    maxScore: 1000,
    percentComplete
  };
};

/**
 * Check if both partners have completed a specific factor
 */
export const checkBothPartnersCompleted = async (partner1Id, partner2Id, factorNumber) => {
  try {
    const partner1Assessment = await getDoc(doc(db, 'assessments', partner1Id));
    const partner2Assessment = await getDoc(doc(db, 'assessments', partner2Id));

    const partner1Completed = partner1Assessment.exists() && 
      partner1Assessment.data().completedFactors?.includes(factorNumber);
    
    const partner2Completed = partner2Assessment.exists() && 
      partner2Assessment.data().completedFactors?.includes(factorNumber);

    return {
      success: true,
      partner1Completed,
      partner2Completed,
      bothCompleted: partner1Completed && partner2Completed,
      partner1Score: partner1Assessment.exists() ? 
        partner1Assessment.data().factorScores?.[factorNumber] || 0 : 0,
      partner2Score: partner2Assessment.exists() ? 
        partner2Assessment.data().factorScores?.[factorNumber] || 0 : 0
    };
  } catch (error) {
    console.error('Error checking partner completion:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Get answers for a specific factor
 */
export const getFactorAnswers = async (userId, factorNumber) => {
  try {
    const assessmentDoc = await getDoc(doc(db, 'assessments', userId));
    
    if (!assessmentDoc.exists()) {
      return { success: false, answers: {}, error: 'Assessment not found' };
    }

    const allAnswers = assessmentDoc.data().answers || {};
    const factorAnswers = {};

    const startQuestion = (factorNumber - 1) * 10 + 1;
    const endQuestion = factorNumber * 10;

    for (let i = startQuestion; i <= endQuestion; i++) {
      if (allAnswers[i] !== undefined) {
        factorAnswers[i] = allAnswers[i];
      }
    }

    return { success: true, answers: factorAnswers, error: null };
  } catch (error) {
    console.error('Error getting factor answers:', error);
    return { success: false, answers: {}, error: error.message };
  }
};

/**
 * Mark assessment as completed (all 20 factors done)
 */
export const markAssessmentComplete = async (userId) => {
  try {
    await updateDoc(doc(db, 'assessments', userId), {
      status: 'completed',
      completedAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    });

    return { success: true, error: null };
  } catch (error) {
    console.error('Error marking assessment complete:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Reset a specific factor (for counselor use)
 */
export const resetFactor = async (userId, factorNumber) => {
  try {
    const assessmentDoc = await getDoc(doc(db, 'assessments', userId));
    if (!assessmentDoc.exists()) {
      return { success: false, error: 'Assessment not found' };
    }

    const currentData = assessmentDoc.data();
    const updatedFactorScores = { ...currentData.factorScores };
    delete updatedFactorScores[factorNumber];

    const updatedCompletedFactors = (currentData.completedFactors || [])
      .filter(f => f !== factorNumber);

    const updatedAnswers = { ...currentData.answers };
    const startQuestion = (factorNumber - 1) * 10 + 1;
    const endQuestion = factorNumber * 10;
    
    for (let i = startQuestion; i <= endQuestion; i++) {
      delete updatedAnswers[i];
    }

    const totalScore = calculateTotalScore(updatedFactorScores);

    await updateDoc(doc(db, 'assessments', userId), {
      answers: updatedAnswers,
      factorScores: updatedFactorScores,
      completedFactors: updatedCompletedFactors,
      totalScore: totalScore,
      updatedAt: serverTimestamp()
    });

    return { success: true, error: null };
  } catch (error) {
    console.error('Error resetting factor:', error);
    return { success: false, error: error.message };
  }
};


// ==========================================
// PRE-MARRIAGE INTENSIVE FUNCTIONS (10 Modules)
// ==========================================

/**
 * Save answer for an intensive module question
 * @param {string} userId - User ID
 * @param {number} questionId - Question ID (1-160)
 * @param {number} value - Answer value (1-5)
 */
export const saveIntensiveAnswer = async (userId, questionId, value) => {
  try {
    await updateDoc(doc(db, 'assessments', userId), {
      [`intensiveAnswers.${questionId}`]: value,
      updatedAt: serverTimestamp()
    });

    console.log(`✅ Saved intensive answer: Q${questionId} = ${value}`);
    return { success: true, error: null };
  } catch (error) {
    console.error('Error saving intensive answer:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Calculate score for an intensive module
 * Handles reverse-scored questions
 * @param {object} answers - All intensive answers
 * @param {array} questions - Module questions with reverseScored flags
 * @returns {number} Module score
 */
export const calculateIntensiveModuleScore = (answers, questions) => {
  let score = 0;
  
  questions.forEach(q => {
    const answer = answers[q.id];
    if (answer !== undefined) {
      // Handle reverse scoring: if reverseScored, invert the score (6 - answer)
      score += q.reverseScored ? (6 - answer) : answer;
    }
  });
  
  return score;
};

/**
 * Complete an intensive module and update scores
 * @param {string} userId - User ID
 * @param {number} moduleNumber - Module number (1-10)
 * @param {number} moduleScore - Calculated module score
 * @param {string} coupleId - Couple ID (optional, for auto-unlock)
 */
export const completeIntensiveModule = async (userId, moduleNumber, moduleScore, coupleId = null) => {
  try {
    const assessmentDoc = await getDoc(doc(db, 'assessments', userId));
    
    if (!assessmentDoc.exists()) {
      // Initialize if doesn't exist
      const userDoc = await getDoc(doc(db, 'users', userId));
      const userData = userDoc.data();
      await initializeAssessment(userId, userData?.coupleId || coupleId);
    }

    // Get current data again after possible initialization
    const currentDoc = await getDoc(doc(db, 'assessments', userId));
    const currentData = currentDoc.data();
    
    const updatedModuleScores = {
      ...(currentData.intensiveModuleScores || {}),
      [moduleNumber]: moduleScore
    };

    // Calculate total intensive score
    const intensiveTotalScore = Object.values(updatedModuleScores)
      .reduce((sum, score) => sum + (score || 0), 0);

    // Update assessment
    await updateDoc(doc(db, 'assessments', userId), {
      [`intensiveModuleScores.${moduleNumber}`]: moduleScore,
      completedIntensiveModules: arrayUnion(moduleNumber),
      intensiveTotalScore: intensiveTotalScore,
      updatedAt: serverTimestamp()
    });

    console.log(`✅ Intensive Module ${moduleNumber} completed with score: ${moduleScore}`);

    // Auto-unlock next module (if module > 1 and module < 10)
    if (coupleId && moduleNumber >= 1 && moduleNumber < 10) {
      const nextModule = moduleNumber + 1;
      const coupleRef = doc(db, 'couples', coupleId);
      
      try {
        await updateDoc(coupleRef, {
          [`intensivePermissions.${nextModule}.canTake`]: true,
          [`intensivePermissions.${nextModule}.unlockedAt`]: serverTimestamp()
        });
        console.log(`✅ Auto-unlocked Intensive Module ${nextModule}`);
      } catch (unlockError) {
        console.error(`Error auto-unlocking Module ${nextModule}:`, unlockError);
      }
    } else if (moduleNumber === 10) {
      console.log(`🎉 All intensive modules completed!`);
    }

    return { success: true, totalScore: intensiveTotalScore, error: null };
  } catch (error) {
    console.error('Error completing intensive module:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Get intensive module answers for a specific module
 * @param {string} userId - User ID
 * @param {array} questions - Array of question objects for the module
 * @returns {object} Answers for the module questions
 */
export const getIntensiveModuleAnswers = async (userId, questions) => {
  try {
    const assessmentDoc = await getDoc(doc(db, 'assessments', userId));
    
    if (!assessmentDoc.exists()) {
      return { success: false, answers: {}, error: 'Assessment not found' };
    }

    const allAnswers = assessmentDoc.data().intensiveAnswers || {};
    const moduleAnswers = {};

    questions.forEach(q => {
      if (allAnswers[q.id] !== undefined) {
        moduleAnswers[q.id] = allAnswers[q.id];
      }
    });

    return { success: true, answers: moduleAnswers, error: null };
  } catch (error) {
    console.error('Error getting intensive module answers:', error);
    return { success: false, answers: {}, error: error.message };
  }
};

/**
 * Check if both partners have completed a specific intensive module
 */
export const checkBothPartnersCompletedIntensive = async (partner1Id, partner2Id, moduleNumber) => {
  try {
    const partner1Assessment = await getDoc(doc(db, 'assessments', partner1Id));
    const partner2Assessment = await getDoc(doc(db, 'assessments', partner2Id));

    const partner1Completed = partner1Assessment.exists() && 
      partner1Assessment.data().completedIntensiveModules?.includes(moduleNumber);
    
    const partner2Completed = partner2Assessment.exists() && 
      partner2Assessment.data().completedIntensiveModules?.includes(moduleNumber);

    return {
      success: true,
      partner1Completed,
      partner2Completed,
      bothCompleted: partner1Completed && partner2Completed,
      partner1Score: partner1Assessment.exists() ? 
        partner1Assessment.data().intensiveModuleScores?.[moduleNumber] || 0 : 0,
      partner2Score: partner2Assessment.exists() ? 
        partner2Assessment.data().intensiveModuleScores?.[moduleNumber] || 0 : 0
    };
  } catch (error) {
    console.error('Error checking partner intensive completion:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Get intensive progress statistics for a user
 */
export const getIntensiveProgressStats = (assessment) => {
  if (!assessment) {
    return {
      questionsAnswered: 0,
      totalQuestions: 160,
      modulesCompleted: 0,
      totalModules: 10,
      currentScore: 0,
      maxScore: 800, // 160 questions * 5 points max
      percentComplete: 0
    };
  }

  const questionsAnswered = Object.keys(assessment.intensiveAnswers || {}).length;
  const modulesCompleted = assessment.completedIntensiveModules?.length || 0;
  const currentScore = assessment.intensiveTotalScore || 0;
  const percentComplete = Math.round((modulesCompleted / 10) * 100);

  return {
    questionsAnswered,
    totalQuestions: 160,
    modulesCompleted,
    totalModules: 10,
    currentScore,
    maxScore: 800,
    percentComplete
  };
};

/**
 * Reset a specific intensive module (for counselor use)
 */
export const resetIntensiveModule = async (userId, moduleNumber, questions) => {
  try {
    const assessmentDoc = await getDoc(doc(db, 'assessments', userId));
    if (!assessmentDoc.exists()) {
      return { success: false, error: 'Assessment not found' };
    }

    const currentData = assessmentDoc.data();
    const updatedModuleScores = { ...(currentData.intensiveModuleScores || {}) };
    delete updatedModuleScores[moduleNumber];

    const updatedCompletedModules = (currentData.completedIntensiveModules || [])
      .filter(m => m !== moduleNumber);

    // Remove answers for this module's questions
    const updatedAnswers = { ...(currentData.intensiveAnswers || {}) };
    questions.forEach(q => {
      delete updatedAnswers[q.id];
    });

    // Recalculate total score
    const intensiveTotalScore = Object.values(updatedModuleScores)
      .reduce((sum, score) => sum + (score || 0), 0);

    await updateDoc(doc(db, 'assessments', userId), {
      intensiveAnswers: updatedAnswers,
      intensiveModuleScores: updatedModuleScores,
      completedIntensiveModules: updatedCompletedModules,
      intensiveTotalScore: intensiveTotalScore,
      updatedAt: serverTimestamp()
    });

    console.log(`✅ Intensive Module ${moduleNumber} reset successfully`);
    return { success: true, error: null };
  } catch (error) {
    console.error('Error resetting intensive module:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Get interpretation level for an intensive module score
 * Based on scoring guidance in the module data
 */
export const getIntensiveInterpretationLevel = (score, scoringGuidance) => {
  if (!scoringGuidance) {
    // Default interpretation if no guidance provided
    if (score >= 65) return 'excellent';
    if (score >= 50) return 'developing';
    return 'vulnerable';
  }

  const { excellent, developing, vulnerable } = scoringGuidance;
  
  if (score >= excellent.range[0] && score <= excellent.range[1]) return 'excellent';
  if (score >= developing.range[0] && score <= developing.range[1]) return 'developing';
  return 'vulnerable';
};
